import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@nextui-org/react";
import piexif from "piexifjs";
import { IS_IOS } from "../utils";
import { trackEvent } from "../../utils/analytics";
import { loadAligner } from "../../utils/documentAligner";
import { BLUR_THRESHOLD, loadOpenCv, runQualityCheck } from "../../utils/documentQuality";
import { checkNinSlipFit } from "../../utils/adaptiveGuide";


// Passport and the NIN ID slip are captured in portrait orientation and need
// to be turned upright; every other document type here is a card shot in
// landscape and needs no rotation.
const ROTATE_TYPES = ["PASSPORT", "NIN_ID_SLIP"];

// How long to wait for the on-device scanner (opencv.js + ORT wasm + aligner
// model, ~11.6MB) before giving up on live detection and offering manual
// capture. The loading overlay blocks the camera until then, so without a
// limit a slow or stalled download would leave the user unable to capture.
const ENGINE_LOAD_TIMEOUT_MS = 20000;

// The camera counts as ready only once the <video> has its first frame (see
// handleFirstFrame). If a live stream never delivers one, treat it as a camera
// failure so the existing auto-retry kicks in, rather than leaving the user on
// the "Starting camera…" overlay indefinitely.
const CAMERA_FIRST_FRAME_TIMEOUT_MS = 10000;

// Real document aspect ratios (width/height) and which axis each on-screen
// guide (components/reusable/frames/*.jsx) anchors to a fixed percentage of
// the container — mirrors each frame's own CSS `aspectRatio` exactly, so the
// auto-capture check validates against the same shape the guide visually
// shows. Keep in sync with the frame components if either changes:
//   PassportFrame.jsx: width 90%, aspectRatio 185/265, maxWidth 90cqh*185/265 (height capped at 90%)
//   A4Frame.jsx:       width 95%, aspectRatio 210/297 (real A4)
//   IdFrame.jsx:       width 98%, aspectRatio 85.6/54 (real ID-1 card)
// Unlike a hardcoded {x1,y1,x2,y2} box, this is resolved against the live
// container aspect ratio (see getOverlayForDocumentType), so it stays
// correct across devices instead of only the one screen it was tuned on.
//
// NIN_ID_SLIP has no entry: its guide takes the shape of the detected slip
// and is validated through utils/adaptiveGuide.js instead.
export const DOCUMENT_FRAME_SPEC = {
  PASSPORT: { anchor: "width", anchorFraction: 0.9, aspectRatio: 185 / 265, maxHeightFraction: 0.9 },
  BANK_STATEMENT: { anchor: "width", anchorFraction: 0.95, aspectRatio: 210 / 297 },
  UTILITY_BILL: { anchor: "width", anchorFraction: 0.95, aspectRatio: 210 / 297 },
  DEFAULT: { anchor: "width", anchorFraction: 0.98, aspectRatio: 85.6 / 54 },
};

/** Computes the {x1,y1,x2,y2} guide bounds for `documentType`, matching
 * exactly what its frame component renders on `video`'s current container
 * size. */
export const getOverlayForDocumentType = (video, documentType) => {
  const spec = DOCUMENT_FRAME_SPEC[documentType] || DOCUMENT_FRAME_SPEC.DEFAULT;
  const containerAspect = video.clientWidth / video.clientHeight;

  let widthFrac;
  let heightFrac;
  if (spec.anchor === "width") {
    widthFrac = spec.anchorFraction;
    heightFrac = (spec.anchorFraction * containerAspect) / spec.aspectRatio;

    // A width-anchored portrait document (e.g. a passport, aspectRatio < 1)
    // grows without bound as the container gets wider relative to its
    // height — on a landscape phone or a desktop webcam, heightFrac can
    // exceed 1 (the guide is taller than the screen), and validateIdCard's
    // minSizeRatio check against that box then can never pass, so
    // auto-capture never fires. Once heightFrac would cross maxHeightFraction,
    // stop growing the width and anchor on height instead, working the width
    // back out from the capped height (mirrors PassportFrame's maxHeight).
    if (spec.maxHeightFraction && heightFrac > spec.maxHeightFraction) {
      heightFrac = spec.maxHeightFraction;
      widthFrac = (spec.maxHeightFraction * spec.aspectRatio) / containerAspect;
    }
  } else {
    heightFrac = spec.anchorFraction;
    widthFrac = (spec.anchorFraction * spec.aspectRatio) / containerAspect;
  }

  const x1 = (1 - widthFrac) / 2;
  const y1 = (1 - heightFrac) / 2;
  return { x1, y1, x2: x1 + widthFrac, y2: y1 + heightFrac };
};

// ?debug=1 readout helper — trims xyxyn-shaped objects to 3 decimals so the
// on-screen dump stays legible.
const round = (box) =>
  box &&
  Object.fromEntries(Object.entries(box).map(([k, v]) => [k, typeof v === "number" ? Number(v.toFixed(3)) : v]));

const rotateImage90 = (base64Image) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.src = base64Image;
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.height; // swap width/height
      canvas.height = img.width;
      const ctx = canvas.getContext("2d");

      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((90 * Math.PI) / 180);
      ctx.drawImage(img, -img.width / 2, -img.height / 2);

      resolve(canvas.toDataURL("image/jpeg", 1));
    };
  });
};

const injectExif = (base64Image) => {
  const zeroth = {};
  const exif = {};

  zeroth[piexif.ImageIFD.Make] = "direct-link";
  zeroth[piexif.ImageIFD.Software] = "enhanced-camera";
  exif[piexif.ExifIFD.DateTimeOriginal] = new Date().toISOString();

  const exifObj = { "0th": zeroth, Exif: exif };
  const exifBytes = piexif.dump(exifObj);

  return piexif.insert(exifBytes, base64Image);
};

/**
 * Ask the camera for the largest mode it actually supports.
 *
 * A fixed `ideal: 1920` is a guess, and a bad one on a phone held in portrait:
 * the constraint is satisfied by a rotated mode whose *width* is only 1080, and
 * an ID card spans the frame width. getCapabilities reports what the device can
 * really do, so request that instead of assuming.
 *
 * resizeMode "none" asks for the sensor output without the browser cropping and
 * scaling it first. Not every browser knows the constraint, so it is retried
 * without on failure. Any error here is non-fatal: the stream keeps whatever
 * resolution it already negotiated.
 */
const requestMaxResolution = async (track) => {
  if (!track?.getCapabilities || !track?.applyConstraints) return null;

  let capabilities;
  try {
    capabilities = track.getCapabilities();
  } catch (err) {
    console.warn("[camera] getCapabilities unavailable:", err.message);
    return null;
  }

  const maxWidth = capabilities?.width?.max;
  const maxHeight = capabilities?.height?.max;
  if (!maxWidth || !maxHeight) return null;

  const before = track.getSettings?.();
  if (before?.width >= maxWidth && before?.height >= maxHeight) return before;

  const target = { width: { ideal: maxWidth }, height: { ideal: maxHeight } };
  try {
    await track.applyConstraints({ ...target, resizeMode: "none" });
  } catch (err) {
    try {
      await track.applyConstraints(target);
    } catch (retryErr) {
      console.warn("[camera] could not raise resolution:", retryErr.message);
      return before;
    }
  }

  const after = track.getSettings?.();
  console.log(
    `[camera] resolution ${before?.width}x${before?.height} -> ${after?.width}x${after?.height} ` +
      `(device max ${maxWidth}x${maxHeight})`
  );
  return after;
};

/**
 * The source rect, in `mediaWidth`x`mediaHeight` native pixel space, that
 * object-cover actually displays inside a `containerWidth`x`containerHeight`
 * box — i.e. the window into the sensor the user is looking at, independent
 * of the document's position (it never depends on where the document is, so
 * a last-second nudge before capture can't produce a bad crop). Shared by
 * mapXyxynToDisplayed (converts a detection box into this same cropped space
 * for on-screen validation) and the capture functions below (crop the saved
 * photo to this same window, so it matches what the user framed against
 * instead of the full uncropped sensor frame, which is wider than the
 * preview whenever the camera's native aspect ratio doesn't match the
 * container's — see captureVideoFrame's comment for what that mismatch was
 * doing to captured photos).
 */
const getVisibleCropRect = (mediaWidth, mediaHeight, containerWidth, containerHeight) => {
  const mediaAspect = mediaWidth / mediaHeight;
  const containerAspect = containerWidth / containerHeight;

  if (mediaAspect > containerAspect) {
    // Media is wider than container → height fills it, width is cropped
    // symmetrically left/right (object-position defaults to center).
    const sWidth = mediaHeight * containerAspect;
    return { sx: (mediaWidth - sWidth) / 2, sy: 0, sWidth, sHeight: mediaHeight };
  }
  // Media is taller than container → width fills it, height is cropped
  // symmetrically top/bottom.
  const sHeight = mediaWidth / containerAspect;
  return { sx: 0, sy: (mediaHeight - sHeight) / 2, sWidth: mediaWidth, sHeight };
};

/**
 * Draw a source rect of `source` onto a canvas at the given destination size
 * and return it as a JPEG data URL. toBlob is used rather than toDataURL
 * because it doesn't run the JPEG encode synchronously on the main thread —
 * the encode of a full 4K frame is heavy enough to visibly stall the UI
 * otherwise. The result still needs to be a data URL string, since that's
 * what piexif and the upload path both consume, so the blob is read back
 * through FileReader.
 */
const captureCanvasFrame = (source, sx, sy, sWidth, sHeight, destWidth, destHeight) =>
  new Promise((resolve) => {
    const canvas = document.createElement("canvas");
    canvas.width = destWidth;
    canvas.height = destHeight;
    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(source, sx, sy, sWidth, sHeight, 0, 0, destWidth, destHeight);
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          resolve(null);
          return;
        }
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(blob);
      },
      "image/jpeg",
      1
    );
  });

// aligner boxes.xyxyn is normalized against the *native* camera frame
// (video.videoWidth/videoHeight), but the <video> element renders with
// object-cover — which, whenever the native aspect ratio doesn't match the
// on-screen container's, crops one axis to fill it. A box normalized
// against the full native frame can therefore sit well inside the guide
// overlay in frame-space while the physical document it describes is
// partially cropped off screen on the axis object-cover is trimming.
// This maps xyxyn into that same displayed/cropped [0,1] space so
// position checks (the live overlay, the auto-capture guide match) agree
// with what the user actually sees, not the uncropped sensor frame.
const mapPointToDisplayed = (video, { x, y }) => {
  const { sx, sy, sWidth, sHeight } = getVisibleCropRect(
    video.videoWidth,
    video.videoHeight,
    video.clientWidth,
    video.clientHeight
  );
  return {
    x: (x - sx / video.videoWidth) / (sWidth / video.videoWidth),
    y: (y - sy / video.videoHeight) / (sHeight / video.videoHeight),
  };
};

const mapXyxynToDisplayed = (video, xyxyn) => {
  const topLeft = mapPointToDisplayed(video, { x: xyxyn.x1, y: xyxyn.y1 });
  const bottomRight = mapPointToDisplayed(video, { x: xyxyn.x2, y: xyxyn.y2 });
  return { x1: topLeft.x, y1: topLeft.y, x2: bottomRight.x, y2: bottomRight.y };
};

const ninSlipMessageKeys = {
  upright: "holdUpright",
  tooClose: "moveBack",
  tooFar: "moveCloser",
  offCenter: "centerDocument",
};

const DocumentCamera = ({ onCapture, documentType, side, debug = false, overlay: Overlay, flowId, verificationId, clientId, accessToken }) => {
  const { t } = useTranslation();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [streaming, setStreaming] = useState(true);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  // Forces the <video> element to unmount and remount on retry (see its key
  // prop below). Some browsers suspend a hidden tab's <video> rendering
  // pipeline independently of the underlying MediaStreamTrack's health, so
  // reassigning srcObject on the same, already-suspended element isn't always
  // enough to bring the picture back even once a fresh stream is attached — a
  // brand new element reliably starts rendering a live stream, which is the
  // same reason the previous react-webcam-based version of this component
  // (which remounted on every retry via its own key prop) didn't hit this.
  const [videoKey, setVideoKey] = useState(0);
  const [isOk, setIsOk] = useState(false);
  const [autoCapturing, setAutoCapturing] = useState(false);
  const [countdownTimer, setCountdownTimer] = useState(null);
  const [boundingBox, setBoundingBox] = useState(null);
  // Ref, not state: read inside the setInterval callback below, whose
  // closure is fixed for the life of that effect run — a state value read
  // there would always see the render it was created on, not the latest one.
  const isApiCallInProgressRef = useRef(false);
  const [captured, setCaptured] = useState(false);
  const [enginesReady, setEnginesReady] = useState(false);
  const [enginesLoading, setEnginesLoading] = useState(true);
  const [validationMessage, setValidationMessage] = useState(
    t("common.documentCamera.initialInstruction")
  );
  const [debugInfo, setDebugInfo] = useState(null);
  // NIN slip only: the shape (long edge / short edge) the guide currently
  // takes, from the corners the detector last saw. null = default shape.
  // Mirrored in a ref because checkDocumentDetection runs from an interval
  // whose closure is fixed (see the polling effect below).
  const [guideAspect, setGuideAspect] = useState(null);
  const guideAspectRef = useRef(null);
  // ?debug=1 works without needing to change how any parent screen renders
  // this component.
  const debugEnabled = debug || new URLSearchParams(window.location.search).get("debug") === "1";

  const timeoutRef = useRef(null);
  const qualityCheckIntervalRef = useRef(null);
  const retryTimeoutRef = useRef(null);
  const cameraReadyRef = useRef(cameraReady);
  const cameraErrorRef = useRef(cameraError);

  useEffect(() => {
    cameraReadyRef.current = cameraReady;
  }, [cameraReady]);

  useEffect(() => {
    cameraErrorRef.current = cameraError;
  }, [cameraError]);

  // Bumped by every stopCamera() call and read back by startCamera() after its
  // await. React 18 StrictMode intentionally double-invokes a mount effect in
  // development (mount, cleanup, mount again) without waiting for any async
  // work in it to settle, so the first getUserMedia() call is still in flight
  // when the second one starts. Without this guard both calls would resolve,
  // both would assign streamRef/videoRef, and both would independently try to
  // renegotiate resolution on their own track — confirmed by testing, this
  // reliably made the resolution upgrade fail on both, not just leak a track.
  const startAttemptRef = useRef(0);

  const firstFrameTimeoutRef = useRef(null);

  const stopCamera = useCallback(() => {
    startAttemptRef.current += 1;
    clearTimeout(firstFrameTimeoutRef.current);
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  const startCamera = useCallback(async () => {
    stopCamera();
    const attempt = startAttemptRef.current;

    if (!navigator.mediaDevices?.getUserMedia) {
      console.error("Camera error: getUserMedia not supported");
      setCameraError(t("common.documentCamera.cameraUnavailable"));
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
          facingMode: "environment",
          width: { ideal: 1920 },
          height: { ideal: 1080 },
          advanced: [{ focusMode: "continuous" }],
        },
      });

      // A newer startCamera() call already won — this one lost the race
      // (see the comment on startAttemptRef above). Stop the stream rather
      // than leave a second live camera device open in the background.
      if (attempt !== startAttemptRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }

      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        // autoPlay only reliably kicks in the first time a <video> element
        // gets a srcObject. This element is reused across retries rather than
        // remounted, and a browser that paused it while the tab was hidden
        // (or backgrounded on minimise) won't resume playback on its own just
        // because a fresh stream was assigned — the stream is live but the
        // element stays paused, rendering as a blank frame. play() is a
        // no-op if it's already playing, and rejects harmlessly if another
        // play()/pause() call interrupts it, which is fine to ignore here.
        videoRef.current.play().catch(() => {});
      }

      // Not ready yet: the stream is attached but the first frame hasn't
      // arrived, and a capture now would get an empty (0x0) video.
      // handleFirstFrame sets cameraReady on the video's loadeddata event.
      firstFrameTimeoutRef.current = setTimeout(() => {
        if (attempt !== startAttemptRef.current) return;
        console.error(`Camera error: no frame within ${CAMERA_FIRST_FRAME_TIMEOUT_MS}ms`);
        setCameraError("Camera produced no frames");
      }, CAMERA_FIRST_FRAME_TIMEOUT_MS);
      setCameraError(null);

      const track = stream.getVideoTracks()[0];
      if (track) {
        track.onmute = () => setCameraError("Camera interrupted");
        track.onended = () => setCameraError("Camera interrupted");
        // Upgrade to the device's best mode once the stream is live. Done
        // after the camera starts rather than as an opening constraint so a
        // device that cannot manage it still gets a working preview.
        requestMaxResolution(track);
      }
    } catch (err) {
      if (attempt !== startAttemptRef.current) return;
      console.error("Camera error:", err);
      setCameraError(err?.message || t("common.documentCamera.cameraUnavailable"));
    }
  }, [stopCamera, t]);

  // loadeddata: the video has its first frame, so a capture now gets a real
  // image. This, not attaching the stream, is what makes the camera ready.
  const handleFirstFrame = useCallback(() => {
    clearTimeout(firstFrameTimeoutRef.current);
    setCameraReady(true);
  }, []);

  useEffect(() => {
    startCamera();
    return stopCamera;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load the on-device aligner model + OpenCV.js once per tab (cached at
  // module scope in the util files, so a front->back remount reuses them
  // without re-downloading). On failure, or if loading takes longer than
  // ENGINE_LOAD_TIMEOUT_MS, there's no live detection possible — drop
  // straight to the manual-capture fallback rather than hanging on the
  // loading overlay.
  useEffect(() => {
    let cancelled = false;
    let loadTimeoutId;
    const loadTimedOut = new Promise((_, reject) => {
      loadTimeoutId = setTimeout(
        () => reject(new Error(`Document scanner engines did not load within ${ENGINE_LOAD_TIMEOUT_MS}ms`)),
        ENGINE_LOAD_TIMEOUT_MS
      );
    });
    Promise.race([Promise.all([loadAligner(clientId, accessToken), loadOpenCv()]), loadTimedOut])
      .then(() => {
        if (cancelled) return;
        setEnginesReady(true);
        setEnginesLoading(false);
      })
      .catch((err) => {
        console.error("Failed to load document scanner engines:", err);
        if (cancelled) return;
        setEnginesLoading(false);
        setStreaming(false);
      })
      .finally(() => clearTimeout(loadTimeoutId));
    return () => {
      cancelled = true;
      clearTimeout(loadTimeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-retry when camera fails to start
  useEffect(() => {
    if (!cameraError) return;
    retryTimeoutRef.current = setTimeout(() => {
      setCameraReady(false);
      setCameraError(null);
      setVideoKey((k) => k + 1);
      startCamera();
    }, 1500);
    return () => clearTimeout(retryTimeoutRef.current);
  }, [cameraError, startCamera]);

  // iOS Safari can mute/end the camera track after the tab is backgrounded
  // (lock screen, app switch) without firing an error, leaving a frozen/blank
  // video feed that reads as "blurry" to the server-side check. Detect that
  // and route it through the same retry as a start-up failure.
  //
  // On iOS specifically (Safari and every other iOS browser — they all run on
  // WebKit), the video element can also paint a stale/blank frame on resume
  // while the MediaStreamTrack itself still reports readyState "live" and
  // muted: false, so the track-health check below never catches it. Always
  // force the retry on resume for iOS rather than relying on track state.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      const track = streamRef.current?.getVideoTracks?.()?.[0];
      const trackUnhealthy = track && (track.readyState !== "live" || track.muted);
      if (trackUnhealthy || IS_IOS) {
        setCameraError("Camera interrupted");
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  // Grabs the current video frame into an offscreen canvas at roughly the
  // on-screen preview size (matches the old capturePreviewFrame, not the
  // full native sensor resolution) — used by the on-device quality check
  // (kept separate from captureFrame()/getScreenshot(), which JPEG-encodes
  // the full native frame for the final captured image). The blur/glare
  // thresholds in documentQuality.js were tuned against a preview-size
  // input: warpPerspective resizes the detected document region to a fixed
  // 800x480, and how much that resize up- or down-scales the source
  // (and therefore how sharp the result measures) depends on the input
  // frame's resolution, not just the document's real sharpness. Downscaling
  // here also cuts the per-frame OpenCV processing cost.
  const grabVideoFrameCanvas = useCallback(() => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return null;
    const width = video.clientWidth || video.videoWidth;
    const height = Math.round(width / (video.videoWidth / video.videoHeight));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    canvas.getContext("2d").drawImage(video, 0, 0, width, height);
    return canvas;
  }, []);

  // NIN slip: the guide takes the shape of the slip the detector sees, and
  // the slip is judged against that guide by its corners (see
  // utils/adaptiveGuide.js) — a fixed guide cannot be filled on both axes by
  // a slip of different proportions, so auto-capture never fired.
  const validateNinSlip = useCallback(
    (video, corners, xyxyn) => {
      const { aspect, guide, fit } = checkNinSlipFit(
        corners.map((p) => mapPointToDisplayed(video, p)),
        video.clientWidth,
        video.clientHeight,
        guideAspectRef.current
      );
      guideAspectRef.current = aspect;
      setGuideAspect(aspect);

      if (debugEnabled) {
        setDebugInfo({
          video: `${video.videoWidth}x${video.videoHeight}`,
          client: `${video.clientWidth}x${video.clientHeight}`,
          raw: xyxyn,
          displayed: mapXyxynToDisplayed(video, xyxyn),
          overlay: {
            x1: guide.x1 / video.clientWidth,
            y1: guide.y1 / video.clientHeight,
            x2: guide.x2 / video.clientWidth,
            y2: guide.y2 / video.clientHeight,
          },
          reason: fit.reason,
          guideAspect: aspect,
          widthRatio: fit.widthRatio,
          heightRatio: fit.heightRatio,
        });
      }

      if (fit.isValid) {
        setIsOk(true);
        return true;
      }
      setValidationMessage(t(`common.documentCamera.${ninSlipMessageKeys[fit.reason]}`));
      setBoundingBox(null);
      setIsOk(false);
      return false;
    },
    [debugEnabled, t]
  );

  // On-device document detection (aligner + blur/glare, see utils/documentQuality.js)
  const checkDocumentDetection = useCallback(
    async () => {
      isApiCallInProgressRef.current = true;

      try {
        const canvas = grabVideoFrameCanvas();
        if (!canvas) {
          setBoundingBox(null);
          setIsOk(false);
          return false;
        }

        const { boxes, corners, has_glare, is_blurry, blur_score, blur_score_normalized } =
          await runQualityCheck(canvas, videoRef.current);

        if (boxes?.xyxyn) {
          setBoundingBox(boxes.xyxyn);

          // Focus the camera on the center of the detected document area
          const { x1, y1, x2, y2 } = boxes.xyxyn;
          const cx = (x1 + x2) / 2;
          const cy = (y1 + y2) / 2;
          const track = streamRef.current?.getVideoTracks?.()?.[0];
          if (track?.getCapabilities?.()?.focusMode?.includes("single-shot")) {
            track.applyConstraints({
              advanced: [{ focusMode: "single-shot", pointsOfInterest: [{ x: cx, y: cy }] }],
            }).catch(() => {});
          }

          // Merged (not replaced) so the blur score is visible in the debug
          // readout even when a blur/glare rejection returns before the
          // positioning debug info below is written.
          if (debugEnabled) {
            setDebugInfo((prev) => ({
              ...prev,
              blurScore: blur_score,
              blurScoreNormalized: blur_score_normalized,
              isBlurry: is_blurry,
            }));
          }

          // Check blur/glare first, regardless of positioning
          if (is_blurry) {
            console.log("is blurry");
            setValidationMessage(t("common.documentCamera.blurryWarning"));
            setIsOk(false);
            return false;
          }
          if (has_glare) {
            console.log("has glare");
            setValidationMessage(t("common.documentCamera.glareWarning"));
            setIsOk(false);
            return false;
          }

          // Validate against what's actually visible on screen, not the
          // uncropped native frame — see mapXyxynToDisplayed. The overlay
          // itself is also computed from the live container size, so it
          // matches the guide's actual on-screen shape (see
          // getOverlayForDocumentType) rather than a hardcoded box tuned to
          // one device.
          const video = videoRef.current;
          if (documentType === "NIN_ID_SLIP" && video && corners) {
            return validateNinSlip(video, corners, boxes.xyxyn);
          }

          const displayedBox = video ? mapXyxynToDisplayed(video, boxes.xyxyn) : boxes.xyxyn;
          const overlay = video ? getOverlayForDocumentType(video, documentType) : null;
          const result = overlay
            ? validateIdCard(displayedBox, overlay)
            : { isValid: false, withinBounds: false, sizeSufficient: false };

          if (debugEnabled) {
            const v = videoRef.current;
            setDebugInfo((prev) => ({
              ...prev,
              video: v ? `${v.videoWidth}x${v.videoHeight}` : "?",
              client: v ? `${v.clientWidth}x${v.clientHeight}` : "?",
              raw: boxes.xyxyn,
              displayed: displayedBox,
              overlay: result.overlay,
              withinBounds: result.withinBounds,
              sizeSufficient: result.sizeSufficient,
              widthRatio: result.widthRatio,
              heightRatio: result.heightRatio,
            }));
          }

          if (result.isValid) {
            setIsOk(true);
            return true;
          }

          if (!result.sizeSufficient) {
            setValidationMessage(t("common.documentCamera.moveCloser"));
          } else if (!result.withinBounds) {
            setValidationMessage(t("common.documentCamera.centerDocument"));
          }
        } else {
          setValidationMessage(t("common.documentCamera.noDocumentDetected"));
        }

        setBoundingBox(null);
        setIsOk(false);
        return false;
      } catch (err) {
        console.error("Document detection error:", err);
        setIsOk(false);
        return false;
      } finally {
        isApiCallInProgressRef.current = false;
      }
    },
    [documentType, grabVideoFrameCanvas, validateNinSlip, debugEnabled, t]
  );

  // Capture at the camera's native resolution — the video element's CSS width
  // (around 430px on a phone) has nothing to do with the frame's actual size,
  // so the canvas is explicitly sized off the video's own videoWidth/videoHeight,
  // not a CSS box size.
  //
  // Cropped to the object-cover-visible window (getVisibleCropRect), not the
  // full native frame. The <video> renders with object-cover, which crops the
  // preview to fill its container whenever the camera's native aspect ratio
  // doesn't match the container's — the user frames their document against
  // that cropped preview, and checkDocumentDetection validates position/size
  // against that same cropped space (see mapXyxynToDisplayed). Capturing the
  // full uncropped native frame instead meant the saved photo had a wider
  // field of view than either of those, so a document that filled the guide
  // on screen ended up occupying a visibly smaller fraction of the saved
  // image — "looks close, comes out looking far away".
  const captureVideoFrame = useCallback(() => {
    const video = videoRef.current;
    if (!video?.videoWidth || !video?.videoHeight) return null;
    const { sx, sy, sWidth, sHeight } = getVisibleCropRect(
      video.videoWidth,
      video.videoHeight,
      video.clientWidth,
      video.clientHeight
    );
    return captureCanvasFrame(video, sx, sy, sWidth, sHeight, sWidth, sHeight);
  }, []);

  // Preferred capture path: the still-photo pipeline, which runs at the sensor's
  // photo resolution rather than its video resolution and is typically several
  // times wider. ImageCapture is Chromium-only, so Safari/iOS always falls back
  // to the video frame above.
  //
  // The still and the preview can have different fields of view on some devices,
  // which would crop the document differently from what the user framed. That is
  // a worse failure than low resolution, so a photo whose aspect ratio does not
  // match the preview is discarded rather than used.
  const captureFrame = useCallback(async () => {
    const video = videoRef.current;
    if (!video?.videoWidth || !video?.videoHeight) return null;

    const track = streamRef.current?.getVideoTracks?.()?.[0];
    if (track && typeof window.ImageCapture === "function") {
      try {
        const blob = await new window.ImageCapture(track).takePhoto();
        // piexif can only rewrite JPEG, and the upload path assumes it too.
        if (blob?.type !== "image/jpeg") {
          throw new Error(`takePhoto returned ${blob?.type || "no blob"}`);
        }

        const dataUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = () => reject(new Error("Failed to read photo blob"));
          reader.readAsDataURL(blob);
        });

        const photo = await new Promise((resolve, reject) => {
          const image = new Image();
          image.onload = () => resolve(image);
          image.onerror = () => reject(new Error("Failed to decode photo"));
          image.src = dataUrl;
        });

        const previewAspect = video.videoWidth / video.videoHeight;
        const photoAspect = photo.naturalWidth / photo.naturalHeight;
        if (Math.abs(photoAspect - previewAspect) / previewAspect > 0.05) {
          throw new Error(
            `photo aspect ${photoAspect.toFixed(3)} differs from preview ${previewAspect.toFixed(3)}`
          );
        }
        if (photo.naturalWidth < video.videoWidth) {
          throw new Error(
            `photo ${photo.naturalWidth}px is no better than the video frame ${video.videoWidth}px`
          );
        }

        console.log(
          `[capture] still photo ${photo.naturalWidth}x${photo.naturalHeight} ` +
            `(video frame is ${video.videoWidth}x${video.videoHeight})`
        );

        // Re-rasterise rather than returning the raw bytes. Some devices'
        // takePhoto() output is the sensor's native (often landscape) pixel
        // layout plus an EXIF Orientation tag telling a viewer how to rotate
        // it for display — browsers apply that correction when decoding for
        // naturalWidth/naturalHeight above, and drawImage uses that same
        // corrected decode. injectExif() further down replaces the entire
        // EXIF segment wholesale (confirmed: piexif.insert's mergeSegments
        // swaps out the whole APP1:Exif block), which silently drops that
        // Orientation tag — the pixels would stay physically sideways with
        // nothing left to say so. Drawing the already-corrected decode onto
        // a canvas bakes the correction into the pixels themselves, so nothing
        // downstream needs to know the tag ever existed.
        //
        // Cropped to the same object-cover-visible window as captureVideoFrame
        // (see its comment), computed against the photo's own resolution —
        // the aspect-ratio check above already confirms the photo shares the
        // preview's aspect within 5%, so the container-relative crop fraction
        // that applies to the preview applies just as well to the photo.
        const photoCrop = getVisibleCropRect(
          photo.naturalWidth,
          photo.naturalHeight,
          video.clientWidth,
          video.clientHeight
        );
        return captureCanvasFrame(
          photo,
          photoCrop.sx,
          photoCrop.sy,
          photoCrop.sWidth,
          photoCrop.sHeight,
          photoCrop.sWidth,
          photoCrop.sHeight
        );
      } catch (err) {
        console.warn("[capture] takePhoto unusable, using video frame:", err.message);
      }
    }

    return captureVideoFrame();
  }, [captureVideoFrame]);

  // Capture, rotate if the document needs it, stamp EXIF, hand it up. Shared by
  // the auto-capture countdown and the manual shutter so the two cannot drift.
  //
  // `preCaptured` is a promise for a frame already being captured: the
  // auto-capture countdown passes the shot taken the moment the document
  // validated, so the photo is the one that passed the check rather than
  // whatever the camera is pointing at when the countdown ends. If it came
  // back empty, a fresh capture is the fallback.
  const emitCapture = useCallback(async (preCaptured) => {
    // Set before awaiting the capture, not after: takePhoto + decode can take a
    // second or more on a phone, and the auto-capture countdown clears
    // autoCapturing as soon as this starts, so the fallback "Take Photo"
    // button would otherwise show (and accept a second tap) until the frame
    // came back.
    setCaptured(true);
    const imageSrc = (preCaptured && (await preCaptured)) || (await captureFrame());
    if (!imageSrc) {
      // Nothing was captured (video not ready) — bring the button back so the
      // user can try again.
      setCaptured(false);
      return;
    }
    const rotated = ROTATE_TYPES.includes(documentType) ? await rotateImage90(imageSrc) : imageSrc;
    const finalImage = injectExif(rotated);

    onCapture(finalImage);
  }, [captureFrame, documentType, onCapture]);

  const validateIdCard = (box, overlay, tolerance = 0.02, minSizeRatio = 0.85) => {
    const overlayWidth = overlay.x2 - overlay.x1;
    const overlayHeight = overlay.y2 - overlay.y1;
    const boxWidth = box.x2 - box.x1;
    const boxHeight = box.y2 - box.y1;

    const isWithinBounds =
      box.x1 >= overlay.x1 - tolerance &&
      box.y1 >= overlay.y1 - tolerance &&
      box.x2 <= overlay.x2 + tolerance &&
      box.y2 <= overlay.y2 + tolerance;

    const isSizeSufficient =
      boxWidth >= overlayWidth * minSizeRatio && boxHeight >= overlayHeight * minSizeRatio;

    return {
      overlay,
      widthRatio: boxWidth / overlayWidth,
      heightRatio: boxHeight / overlayHeight,
      isValid: isWithinBounds && isSizeSufficient,
      withinBounds: isWithinBounds,
      sizeSufficient: isSizeSufficient,
    };
  };

  const countdownRef = useRef(null);

  const triggerAutoCapture = useCallback(() => {
    // Prevent multiple countdowns from stacking
    if (countdownRef.current) return;

    console.log("Auto-capture triggered - starting 3-second countdown");
    setAutoCapturing(true);

    // Take the photo now, while the document is in the validated position;
    // the countdown only delays handing it on, so moving the camera during it
    // cannot change what is captured.
    const shot = captureFrame().catch((err) => {
      console.error("Capture at auto-capture trigger failed:", err);
      return null;
    });

    let countdown = 3;
    setCountdownTimer(countdown);

    countdownRef.current = setInterval(() => {
      countdown--;

      if (countdown <= 0) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
        setCountdownTimer(null);

        emitCapture(shot);

        setAutoCapturing(false);
        setStreaming(false);
      } else {
        setCountdownTimer(countdown);
      }
    }, 1000);
  }, [captureFrame, emitCapture]);

  const getScaledBox = () => {
    const video = videoRef.current;
    if (!video) return null;

    const displayed = mapXyxynToDisplayed(video, boundingBox);
    const containerWidth = video.clientWidth;
    const containerHeight = video.clientHeight;

    return {
      x1: displayed.x1 * containerWidth,
      y1: displayed.y1 * containerHeight,
      x2: displayed.x2 * containerWidth,
      y2: displayed.y2 * containerHeight,
    };
  };

  useEffect(() => {
    if (!streaming || !enginesReady) return; // wait for camera + on-device engines
    console.log("Starting simple document detection...");

    // Timeout after 60 seconds if nothing detected
    timeoutRef.current = setTimeout(() => {
      console.log("Auto-capture timeout reached");
      clearInterval(qualityCheckIntervalRef.current);
      setStreaming(false);
    }, 60000);

    // Poll every 1s to check for document
    qualityCheckIntervalRef.current = setInterval(async () => {
      if (isApiCallInProgressRef.current || !cameraReadyRef.current || cameraErrorRef.current) return;

      const isDocumentDetected = await checkDocumentDetection();
      if (isDocumentDetected) {
        console.log("Document detected!");
        clearTimeout(timeoutRef.current);
        triggerAutoCapture();
        setStreaming(false);
      }
    }, 1000);

    // Cleanup only clears timers — must stay side-effect-free since React 18
    // Strict Mode invokes it once synchronously right after the initial
    // setup (mount → cleanup → mount again) before any state update from it
    // is flushed. A setStreaming(false) here previously fired during that
    // phantom cleanup and killed polling before its first tick ever ran.
    return () => {
      clearInterval(qualityCheckIntervalRef.current);
      clearTimeout(timeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streaming, enginesReady]);

  const handleManualCapture = () => {
    trackEvent("document_manual_capture_clicked", { flowId, verificationId, side, documentType });
    emitCapture();
  };

  return (
    <div className="flex h-full flex-col">
      <div className="relative h-full flex flex-col">
        <video
          key={videoKey}
          ref={videoRef}
          onLoadedData={handleFirstFrame}
          autoPlay
          playsInline
          muted
          className="w-full h-full object-cover"
        />

        {/* Loading overlay — camera stream and/or on-device scanner not yet ready */}
        {(!cameraReady || enginesLoading) && !cameraError && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/70 gap-3">
            <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin" />
            <span className="text-white text-sm font-medium">{t("common.documentCamera.startingCamera")}</span>
          </div>
        )}

        {/* Error overlay — auto-retrying */}
        {cameraError && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/70 gap-3">
            <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin" />
            <span className="text-white text-sm font-medium">{t("common.documentCamera.retryingCamera")}</span>
          </div>
        )}

        {/* Live document outline — shows what the camera has detected */}
        {boundingBox && (
          <svg
            className="absolute inset-0 w-full h-full"
            style={{ pointerEvents: "none" }}
          >
            {(() => {
              const box = getScaledBox();
              return (
                <rect
                  x={box.x1}
                  y={box.y1}
                  width={box.x2 - box.x1}
                  height={box.y2 - box.y1}
                  fill="none"
                  stroke={isOk ? "#3b82f6" : "rgba(255,255,255,0.7)"}
                  strokeWidth="2"
                  strokeDasharray={isOk ? "none" : "6 4"}
                  rx="4"
                />
              );
            })()}
          </svg>
        )}

        {/* Document overlay - corners turn blue when quality is good */}
        <Overlay isOk={isOk} documentType={documentType} side={side} guideAspect={guideAspect} />

        {/* Auto-capture countdown */}
        {countdownTimer && (
          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50">
            <div className="text-white text-6xl font-bold animate-pulse">
              {countdownTimer}
            </div>
          </div>
        )}

        {/* Fallback: manual capture after auto-detection times out, or when the
            on-device scanner fails to load. Only once the camera is actually
            producing frames — the scanner can fail before the camera starts,
            and a tap then would capture nothing. */}
        {!autoCapturing && !streaming && !captured && cameraReady && !cameraError && (
          <>
            <div className="absolute top-0 left-0 right-0 flex justify-center pt-5 px-4">
              <div className="px-5 py-3 rounded-xl text-sm font-semibold text-center bg-black/80 text-white shadow-lg">
                {t("common.documentCamera.positionInFrame")}
              </div>
            </div>
            <div className="absolute bottom-4 left-4 right-4 z-10">
              <Button
                color="primary"
                radius="sm"
                onClick={handleManualCapture}
                size="lg"
                className="w-full"
              >
                {t("common.documentCamera.takePhoto")}
              </Button>
            </div>
          </>
        )}

        {/* Instructions - pinned to top so users see them immediately */}
        {streaming && (
          <div className="absolute top-0 left-0 right-0 flex justify-center pt-5 px-4">
            <div className={`px-5 py-3 rounded-xl text-sm font-semibold text-center transition-all duration-300 shadow-lg ${
              isOk ? "bg-blue-500 text-white" : "bg-black/80 text-white"
            }`}>
              {isOk ? t("common.documentCamera.documentDetected") : validationMessage}
            </div>
          </div>
        )}

        {/* ?debug=1 readout — the actual numbers behind each pass/fail, so a
            positioning bug can be diagnosed from real values instead of
            guessed at from a screenshot. */}
        {debugEnabled && debugInfo && (
          <pre className="absolute bottom-0 left-0 right-0 z-20 m-0 max-h-[45%] overflow-auto bg-black/85 p-2 text-[10px] leading-tight text-lime-300">
{JSON.stringify(
  {
    blurScore: debugInfo.blurScore?.toFixed(1),
    blurScoreNormalized: debugInfo.blurScoreNormalized?.toFixed(3),
    blurThreshold: BLUR_THRESHOLD,
    isBlurry: debugInfo.isBlurry,
    video: debugInfo.video,
    client: debugInfo.client,
    raw: round(debugInfo.raw),
    displayed: round(debugInfo.displayed),
    overlay: round(debugInfo.overlay),
    withinBounds: debugInfo.withinBounds,
    sizeSufficient: debugInfo.sizeSufficient,
    reason: debugInfo.reason,
    guideAspect: debugInfo.guideAspect?.toFixed(3),
    widthRatio: debugInfo.widthRatio?.toFixed(3),
    heightRatio: debugInfo.heightRatio?.toFixed(3),
  },
  null,
  1
)}
          </pre>
        )}
      </div>
    </div>
  );
};

export default DocumentCamera;
